import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ChartRecommendation } from './dataAnalyzer';
import { Insight } from './insightsAnalyzer';
import { KPI } from './kpiAnalyzer';

export interface PDFExportOptions {
  fileName: string;
  title: string;
  kpis: KPI[];
  charts: Array<{
    recommendation: ChartRecommendation;
    insights: Insight[];
    elementId: string;
  }>;
  includeRawData?: boolean;
  includeDataSummary?: boolean;
}

export class PDFExporter {
  private pdf: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private currentY: number;
  private lineHeight: number;

  constructor() {
    this.pdf = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
    this.margin = 20;
    this.currentY = this.margin;
    this.lineHeight = 6;
  }

  async exportToPDF(options: PDFExportOptions): Promise<void> {
    try {
      // Header
      this.addHeader(options.title, options.fileName);
      
      // Executive Summary (KPIs)
      this.addKPISection(options.kpis);
      
      // Charts and Insights
      for (let i = 0; i < options.charts.length; i++) {
        const chart = options.charts[i];
        await this.addChartSection(chart, i + 1);
        
        if (i < options.charts.length - 1) {
          this.addPageBreak();
        }
      }
      
      // Footer
      this.addFooter();
      
      // Download
      this.pdf.save(`${options.fileName.replace(/\.[^/.]+$/, '')}_analysis.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
      throw new Error('Failed to generate PDF report');
    }
  }

  private addHeader(title: string, fileName: string): void {
    // Logo area (placeholder)
    this.pdf.setFillColor(59, 130, 246);
    this.pdf.rect(this.margin, this.margin, 50, 12, 'F');
    
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(14);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('VizData', this.margin + 2, this.margin + 8);
    
    // Title
    this.pdf.setTextColor(31, 41, 55);
    this.pdf.setFontSize(24);
    this.pdf.setFont('helvetica', 'bold');
    this.currentY = this.margin + 25;
    this.pdf.text(title, this.margin, this.currentY);
    
    // Subtitle
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(107, 114, 128);
    this.currentY += 8;
    this.pdf.text(`Data Analysis Report - ${fileName}`, this.margin, this.currentY);
    
    // Date
    this.currentY += 6;
    this.pdf.text(`Generated on ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`, this.margin, this.currentY);
    
    // Separator line
    this.currentY += 10;
    this.pdf.setDrawColor(229, 231, 235);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
    this.currentY += 15;
  }

  private addKPISection(kpis: KPI[]): void {
    // Section Title
    this.pdf.setFontSize(18);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(31, 41, 55);
    this.pdf.text('Executive Summary', this.margin, this.currentY);
    this.currentY += 12;
    
    // KPI Cards
    const cardWidth = (this.pageWidth - 2 * this.margin - 15) / 2; // 2 columns with gap
    const cardHeight = 25;
    
    for (let i = 0; i < kpis.slice(0, 4).length; i++) {
      const kpi = kpis[i];
      const col = i % 2;
      const row = Math.floor(i / 2);
      
      const x = this.margin + col * (cardWidth + 5);
      const y = this.currentY + row * (cardHeight + 5);
      
      // Card background
      this.pdf.setFillColor(248, 250, 252);
      this.pdf.setDrawColor(226, 232, 240);
      this.pdf.rect(x, y, cardWidth, cardHeight, 'FD');
      
      // KPI Value
      this.pdf.setFontSize(20);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(59, 130, 246);
      this.pdf.text(kpi.formattedValue || String(kpi.value), x + 3, y + 8);
      
      // KPI Title
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(31, 41, 55);
      this.pdf.text(kpi.title, x + 3, y + 15);
      
      // KPI Description
      if (kpi.description) {
        this.pdf.setFontSize(9);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(107, 114, 128);
        const lines = this.pdf.splitTextToSize(kpi.description, cardWidth - 6);
        this.pdf.text(lines.slice(0, 2), x + 3, y + 19); // Max 2 lines
      }
    }
    
    this.currentY += Math.ceil(kpis.slice(0, 4).length / 2) * (cardHeight + 5) + 15;
  }

  private async addChartSection(chart: { recommendation: ChartRecommendation; insights: Insight[]; elementId: string }, chartNumber: number): Promise<void> {
    // Check if we need a new page
    if (this.currentY > this.pageHeight - 100) {
      this.addPageBreak();
    }
    
    // Chart Title
    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(31, 41, 55);
    this.pdf.text(`${chartNumber}. ${chart.recommendation.title}`, this.margin, this.currentY);
    this.currentY += 8;
    
    // Chart Description
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(107, 114, 128);
    const descLines = this.pdf.splitTextToSize(chart.recommendation.description, this.pageWidth - 2 * this.margin);
    this.pdf.text(descLines, this.margin, this.currentY);
    this.currentY += descLines.length * 4 + 8;
    
    // Capture chart image
    try {
      const chartElement = document.getElementById(chart.elementId);
      if (chartElement) {
        const canvas = await html2canvas(chartElement, {
          backgroundColor: '#ffffff',
          scale: 1.15,
          logging: false,
          allowTaint: true,
          useCORS: true
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = this.pageWidth - 2 * this.margin;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Check if image fits on current page
        if (this.currentY + imgHeight > this.pageHeight - this.margin) {
          this.addPageBreak();
        }
        
        this.pdf.addImage(imgData, 'PNG', this.margin, this.currentY, imgWidth, imgHeight);
        this.currentY += imgHeight + 10;
      }
    } catch (error) {
      console.warn('Failed to capture chart image:', error);
      // Add placeholder
      this.pdf.setFillColor(248, 250, 252);
      this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 60, 'F');
      this.pdf.setTextColor(107, 114, 128);
      this.pdf.text('Chart image unavailable', this.margin + 5, this.currentY + 30);
      this.currentY += 70;
    }
    
    // Insights Section
    if (chart.insights.length > 0) {
      this.currentY += 5;
      this.pdf.setFontSize(14);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(31, 41, 55);
      this.pdf.text('Key Insights', this.margin, this.currentY);
      this.currentY += 10;
      
      chart.insights.slice(0, 5).forEach((insight, index) => {
        // Check if we need a new page
        if (this.currentY > this.pageHeight - 40) {
          this.addPageBreak();
        }
        
        // Insight bullet point
        this.pdf.setFontSize(10);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(59, 130, 246);
        this.pdf.text('•', this.margin, this.currentY);
        
        // Insight title
        this.pdf.setTextColor(31, 41, 55);
        this.pdf.text(insight.title, this.margin + 5, this.currentY);
        this.currentY += 5;
        
        // Insight description
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(75, 85, 99);
        const lines = this.pdf.splitTextToSize(insight.description, this.pageWidth - 2 * this.margin - 10);
        this.pdf.text(lines, this.margin + 5, this.currentY);
        this.currentY += lines.length * 4 + 3;
        
        // Confidence and recommendation
        if (insight.confidence || insight.recommendation) {
          this.pdf.setFontSize(9);
          this.pdf.setTextColor(107, 114, 128);
          let details = '';
          if (insight.confidence) {
            details += `Confidence: ${insight.confidence}`;
          }
          if (insight.recommendation) {
            if (details) details += ' | ';
            details += `Action: ${insight.recommendation}`;
          }
          this.pdf.text(details, this.margin + 5, this.currentY);
          this.currentY += 8;
        }
      });
    }
  }

  private addPageBreak(): void {
    this.pdf.addPage();
    this.currentY = this.margin;
  }

  private addFooter(): void {
    const pageCount = this.pdf.internal.pages.length - 1; // Subtract 1 for the first empty page
    
    for (let i = 1; i <= pageCount; i++) {
      this.pdf.setPage(i);
      
      // Footer line
      this.pdf.setDrawColor(229, 231, 235);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.pageHeight - 15, this.pageWidth - this.margin, this.pageHeight - 15);
      
      // Footer text
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(107, 114, 128);
      this.pdf.text('Generated by VizData - Data Visualization Platform', this.margin, this.pageHeight - 8);
      
      // Page number
      this.pdf.text(`Page ${i} of ${pageCount}`, this.pageWidth - this.margin - 20, this.pageHeight - 8);
    }
  }

  static async downloadPDF(
    fileName: string,
    title: string,
    kpis: KPI[],
    charts: Array<{ recommendation: ChartRecommendation; insights: Insight[]; elementId: string }>
  ): Promise<void> {
    const exporter = new PDFExporter();
    await exporter.exportToPDF({
      fileName,
      title,
      kpis,
      charts
    });
  }
}
